import React from "react";
import { motion } from "framer-motion";
import { Plus, Minus, Maximize2 } from "lucide-react";

export const VIEW_W = 820;
export const VIEW_H = 550;
const WAVES = Array.from({ length: 20 }, (_, i) => 28 + i * 30);
const PIN_SELECTED = { scale: 1.5 };
const PIN_IDLE = { scale: 1 };
const PIN_SPRING = { type: "spring", stiffness: 320, damping: 20 };
const PIN_HOVER = { scale: 1.8 };
const PIN_STYLE = { cursor: "pointer", transformOrigin: "0px 0px" };

/** Hatch + sea gradient + one flag pattern per Arab country (stretched over its bbox). */
export function MapDefs({ shapes, codeToCountry }) {
  return (
    <defs>
      <pattern id="hatch-disputed" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" fill="transparent" />
        <line x1="0" y1="0" x2="0" y2="6" stroke="#7A0A15" strokeWidth="2.5" />
      </pattern>
      <radialGradient id="sea-grad" cx="50%" cy="50%" r="75%">
        <stop offset="0%" stopColor="#E4EDF3" />
        <stop offset="100%" stopColor="#CBD9E2" />
      </radialGradient>
      {shapes.map((s) => codeToCountry[s.code] && (
        <pattern key={s.code} id={`flag-${s.code}`} patternUnits="userSpaceOnUse" x={s.bbox.x} y={s.bbox.y} width={s.bbox.w} height={s.bbox.h}>
          <image href={codeToCountry[s.code].flag_url} x={0} y={0} width={s.bbox.w} height={s.bbox.h} preserveAspectRatio="none" />
        </pattern>
      ))}
    </defs>
  );
}

export function Sea() {
  return (
    <>
      <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#sea-grad)" />
      <g opacity="0.28" stroke="#A9BFCC" strokeWidth="0.5" fill="none">
        {WAVES.map((y) => (
          <path key={y} d={`M 0 ${y} Q 60 ${y - 10} 120 ${y} T 240 ${y} T 360 ${y} T 480 ${y} T 600 ${y} T 820 ${y}`} />
        ))}
      </g>
    </>
  );
}

export function Compass() {
  return (
    <g transform={`translate(${VIEW_W - 60}, 46)`} opacity="0.6">
      <circle r="22" fill="#F4F1EA" stroke="#8A8480" strokeWidth="0.8" />
      <path d="M 0,-18 L 4,0 L 0,4 L -4,0 Z" fill="#C86641" />
      <path d="M 0,18 L 4,0 L 0,-4 L -4,0 Z" fill="#2B2726" opacity="0.5" />
      <text textAnchor="middle" y="-24" fontSize="9" fill="#5C5652" fontFamily="Playfair Display">N</text>
    </g>
  );
}

const HATCH_SWATCH = { background: "repeating-linear-gradient(45deg,#7A0A15 0 2px,transparent 2px 5px)" };

export function Legend({ t, memories = false }) {
  return (
    <div className="absolute bottom-2 start-2 sm:bottom-3 sm:start-3 flex flex-wrap items-center gap-2 sm:gap-3 bg-card/85 backdrop-blur px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full border border-stoneLine text-[10px] sm:text-xs">
      <span className="inline-flex items-center gap-1.5">
        <img src="https://flagcdn.com/sa.svg" alt="" className="w-4 h-3 object-cover rounded-sm border border-stoneLine" />
        {t("all_countries")}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="w-3 h-3 rounded-sm" style={HATCH_SWATCH} />
        {t("disputed")}
      </span>
      {memories && (
        <span className="inline-flex items-center gap-1.5" data-testid="legend-memories">
          <span className="w-2.5 h-2.5 rounded-full bg-ochre ring-2 ring-ochre/30" />
          {t("memories_legend")}
        </span>
      )}
    </div>
  );
}

export function ZoomControls({ k, zoomIn, zoomOut, reset }) {
  const btn = "w-9 h-9 sm:w-8 sm:h-8 flex items-center justify-center text-ink hover:bg-sandDeep/60 disabled:opacity-30 transition-colors";
  return (
    <div className="absolute top-2 left-2 sm:top-3 sm:left-3 flex flex-col rounded-xl border border-stoneLine bg-card/90 backdrop-blur overflow-hidden shadow-sm" data-testid="map-zoom">
      <button onClick={zoomIn} disabled={k >= 5.9} aria-label="zoom in" className={btn} data-testid="map-zoom-in"><Plus className="w-4 h-4" /></button>
      <button onClick={zoomOut} disabled={k <= 1.01} aria-label="zoom out" className={`${btn} border-t border-stoneLine`} data-testid="map-zoom-out"><Minus className="w-4 h-4" /></button>
      {k > 1.01 && (
        <button onClick={reset} aria-label="reset zoom" className={`${btn} border-t border-stoneLine`} data-testid="map-zoom-reset"><Maximize2 className="w-4 h-4" /></button>
      )}
    </div>
  );
}

/** Circular flag badge for micro-states whose polygon is barely visible. Counter-scaled so it stays tappable at any zoom. */
export function CalloutPin({ country, selected, onSelect, k = 1 }) {
  const size = 22;
  const half = size / 2;
  return (
    <g transform={`translate(${country.x}, ${country.y}) scale(${1 / Math.sqrt(k)})`} data-testid={`flag-pin-${country.code}`}>
      <motion.g whileHover={PIN_HOVER} animate={selected ? PIN_SELECTED : PIN_IDLE} transition={PIN_SPRING} style={PIN_STYLE} onClick={() => onSelect(country.code)}>
        <defs>
          <clipPath id={`clip-${country.code}`}><circle cx={0} cy={0} r={half} /></clipPath>
        </defs>
        <circle cx={0} cy={0} r={half + 8} fill="transparent" />
        <circle cx={0} cy={0} r={half + 1.5} fill="#FFF" stroke="#2B2726" strokeWidth={0.9} />
        <image href={country.flag_url} x={-half} y={-half} width={size} height={size} clipPath={`url(#clip-${country.code})`} preserveAspectRatio="xMidYMid slice" />
      </motion.g>
    </g>
  );
}

/** Pulsing ochre dot on countries that have approved visitor photos/videos ("memories"). Counter-scaled like the pins. */
export function MemoryDot({ dot, label, onSelect, k = 1 }) {
  return (
    <g transform={`translate(${dot.x}, ${dot.y}) scale(${1 / Math.sqrt(k)})`} style={PIN_STYLE} onClick={() => onSelect(dot.code)} data-testid={`memory-dot-${dot.code}`}>
      <title>{label}</title>
      <circle r="9" fill="#C86641" opacity="0.35">
        <animate attributeName="r" values="6;13;6" dur="2.4s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.45;0;0.45" dur="2.4s" repeatCount="indefinite" />
      </circle>
      <circle r={dot.count > 1 ? 7 : 4.5} fill="#C86641" stroke="#FFF" strokeWidth="1.2" />
      {dot.count > 1 && (
        <text textAnchor="middle" dominantBaseline="central" fontSize="7.5" fontWeight="700" fill="#FFF" fontFamily="sans-serif" pointerEvents="none">{dot.count}</text>
      )}
    </g>
  );
}
